import { format, startOfMonth } from 'date-fns';
import { localDateToDate } from '../../../../../utils/localDateToDate';
import { allTimestampsOneYear } from '../../../../../utils/allTimestampsOneYear';
import { mapPackageTypeName } from '../../../../../utils/mapPackageTypeName';
import { mapSpendTypename } from '../../../../../utils/mapSpendTypename';
import { ClientAdProductType, CustomerAnalyticsAdspendCategory } from '@zg-rentals/ts-customer-analytics-client';
import type {
  PortfolioAnalyticsPropertyResponseDto,
  SfhAccountLevelSpendResponseDto,
} from '@zg-rentals/ts-customer-analytics-client';

const PACKAGE_TO_SPEND_CATEGORIES: Partial<Record<ClientAdProductType, Array<CustomerAnalyticsAdspendCategory>>> = {
  [ClientAdProductType.BASE_PACKAGE]: [CustomerAnalyticsAdspendCategory.BASE],
  [ClientAdProductType.ENHANCED_PACKAGE]: [CustomerAnalyticsAdspendCategory.ENHANCED],
  [ClientAdProductType.PREMIUM_PACKAGE]: [CustomerAnalyticsAdspendCategory.PREMIUM],
  [ClientAdProductType.PREMIUM_PLUS_PACKAGE]: [CustomerAnalyticsAdspendCategory.PREMIUM_PLUS],
  [ClientAdProductType.SIGNATURE_PACKAGE]: [CustomerAnalyticsAdspendCategory.SIGNATURE],
  [ClientAdProductType.PAY_PER_LEAD]: [CustomerAnalyticsAdspendCategory.PAY_PER_LEAD],
  [ClientAdProductType.FLAT_FEE]: [CustomerAnalyticsAdspendCategory.MFH_FLAT_FEE],
  [ClientAdProductType.FLAT_FEE_FEED_CONNECT]: [
    CustomerAnalyticsAdspendCategory.FLAT_FEE_FEED_CONNECT,
    CustomerAnalyticsAdspendCategory.DAILY_FEED_CONNECT,
  ],
  [ClientAdProductType.INTEGRATED_PAY_PER_LEASE]: [CustomerAnalyticsAdspendCategory.INTEGRATED_PAY_PER_LEASE],
  [ClientAdProductType.LEASE_UP]: [CustomerAnalyticsAdspendCategory.LEASE_UP],
  [ClientAdProductType.PAY_PER_CLICK]: [CustomerAnalyticsAdspendCategory.PAY_PER_CLICK],
  [ClientAdProductType.PAY_PER_LEASE]: [CustomerAnalyticsAdspendCategory.LEGACY_PAY_PER_LEASE],
  [ClientAdProductType.CONCIERGE_PAY_PER_LEASE]: [CustomerAnalyticsAdspendCategory.LEGACY_PAY_PER_LEASE],
  [ClientAdProductType.PACKAGE_MEDIA_ADD_ON]: [CustomerAnalyticsAdspendCategory.MEDIA_ADD_ON],
  [ClientAdProductType.LISTING_SPOTLIGHT]: [CustomerAnalyticsAdspendCategory.LISTING_SPOTLIGHT],
  [ClientAdProductType.SINGLE_FAMILY]: [CustomerAnalyticsAdspendCategory.SFH_LEGACY],
  [ClientAdProductType.SINGLE_FAMILY_FLAT_FEE]: [CustomerAnalyticsAdspendCategory.SFH_FLAT_FEE_LEGACY],
  [ClientAdProductType.SINGLE_FAMILY_DAILY_LISTINGS]: [CustomerAnalyticsAdspendCategory.DAILY_FEED_CONNECT],
};

export type AdvertisingSpendChartData = {
  name: string;
  timestamp: number;
  packageType?: ClientAdProductType;
  totalSpend: number;
  totalProspects?: number;
  costPerProspect?: number;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  [key: string]: any;
};

export type PackageTypeInfo = {
  packageType: CustomerAnalyticsAdspendCategory | 'SFH_BOOST';
  packageTypeString: string;
};

export type AdSpendMode = 'total-spend' | 'cost-per-prospect';

type GenerateAdvertisingSpendDataParams = {
  filteredProperties: Array<PortfolioAnalyticsPropertyResponseDto>;
  sfhAccountLevelSpendData?: Array<SfhAccountLevelSpendResponseDto>;
  noFiltersSelected: boolean;
  selectedPackage?: ClientAdProductType | string;
  hasSelectedProperty?: boolean;
};

type GenerateAdvertisingSpendDataResult = {
  chartData: Array<AdvertisingSpendChartData>;
  allPackageTypesPresent: Array<PackageTypeInfo>;
};

export const generateAdvertisingSpendData = ({
  filteredProperties,
  sfhAccountLevelSpendData,
  noFiltersSelected,
  selectedPackage,
  hasSelectedProperty,
}: GenerateAdvertisingSpendDataParams): GenerateAdvertisingSpendDataResult => {
  const allPackageTypesPresent: Array<PackageTypeInfo> = [];
  const graphDataMap: Record<string, AdvertisingSpendChartData> = {};
  const timestamps = allTimestampsOneYear();
  const isListingSpotlightSelected = selectedPackage === ClientAdProductType.LISTING_SPOTLIGHT;

  timestamps.forEach((timestamp) => {
    const formattedDate = format(new Date(timestamp), 'MMM yyyy');
    graphDataMap[timestamp] = {
      name: formattedDate,
      timestamp,
      packageType: ClientAdProductType.BASE_PACKAGE,
      totalSpend: 0,
      totalProspects: 0,
      costPerProspect: 0,
    };
  });

  // Process regular property data
  filteredProperties.forEach((property) => {
    property.breakdownStats.forEach((stat) => {
      if (isListingSpotlightSelected) {
        return;
      }
      if (stat.adproductTypes.every((type) => type === ClientAdProductType.LISTING_SPOTLIGHT)) {
        return;
      }
      if (stat.adproductTypes.length === 0) {
        return;
      }
      const date = startOfMonth(localDateToDate(stat.statDate));
      const timestamp = date.getTime();

      const key = timestamp;
      if (!graphDataMap[key]) {
        return;
      }

      // Calculate total prospects (direct leads + web referrals)
      const directLeads = stat.emailLeadCount + stat.phoneLeadCount;
      const webReferrals = stat.listingWebReferrals;
      const totalProspects = directLeads + webReferrals;

      if (!graphDataMap[key].totalProspects) {
        graphDataMap[key].totalProspects = 0;
      }
      graphDataMap[key].totalProspects! += totalProspects;

      const allowedCategories =
        selectedPackage && !hasSelectedProperty
          ? PACKAGE_TO_SPEND_CATEGORIES[selectedPackage as ClientAdProductType]
          : undefined;

      Object.entries(stat.spendByCategory).forEach(([category, cost]) => {
        if (!cost) return;
        if (allowedCategories && !allowedCategories.includes(category as CustomerAnalyticsAdspendCategory)) return;
        const categoryString = mapSpendTypename(category as CustomerAnalyticsAdspendCategory);
        const costValue = typeof cost === 'number' ? cost : Number(cost);

        if (!graphDataMap[key][categoryString]) {
          graphDataMap[key][categoryString] = 0;
        }
        graphDataMap[key][categoryString] += costValue;
        graphDataMap[key].totalSpend += costValue;

        if (!allPackageTypesPresent.some((item) => item.packageType === category)) {
          allPackageTypesPresent.push({
            packageType: category as CustomerAnalyticsAdspendCategory,
            packageTypeString: categoryString,
          });
        }
      });
    });
  });

  // Process SFH account level spend data
  if (sfhAccountLevelSpendData) {
    const sfhBoostKey = 'SFH Boost';
    const sfhBoostAdded = { added: false };
    const spotlightAdded = { added: false };

    sfhAccountLevelSpendData.forEach((monthData) => {
      const date = startOfMonth(localDateToDate(monthData.monthDate));
      const timestamp = date.getTime();

      if (!graphDataMap[timestamp]) return;

      // Process boost costs only if not filtering for listing spotlight
      if (!isListingSpotlightSelected && noFiltersSelected) {
        const totalBoostCost = monthData.totalBoostSpend;
        if (totalBoostCost > 0) {
          graphDataMap[timestamp].totalSpend += totalBoostCost;
          if (!graphDataMap[timestamp][sfhBoostKey]) {
            graphDataMap[timestamp][sfhBoostKey] = 0;
          }

          graphDataMap[timestamp][sfhBoostKey] += totalBoostCost;

          if (!sfhBoostAdded.added) {
            allPackageTypesPresent.push({
              packageType: 'SFH_BOOST',
              packageTypeString: sfhBoostKey,
            });
            sfhBoostAdded.added = true;
          }
        }
      }

      // Process spotlight spend when listing spotlight is selected OR when no filters are selected
      if (isListingSpotlightSelected || noFiltersSelected) {
        const spotlightSpend =
          typeof monthData.spotlightSpend === 'number' ? monthData.spotlightSpend : monthData.spotlightSpend || 0;

        if (spotlightSpend > 0) {
          const spotlightKey = mapPackageTypeName(ClientAdProductType.LISTING_SPOTLIGHT);

          graphDataMap[timestamp].totalSpend += spotlightSpend;
          if (!graphDataMap[timestamp][spotlightKey]) {
            graphDataMap[timestamp][spotlightKey] = 0;
          }

          graphDataMap[timestamp][spotlightKey] += spotlightSpend;

          if (!spotlightAdded.added) {
            allPackageTypesPresent.push({
              packageType: CustomerAnalyticsAdspendCategory.LISTING_SPOTLIGHT,
              packageTypeString: spotlightKey,
            });
            spotlightAdded.added = true;
          }
        }
      }
    });
  }

  const currentDateFormatted = format(new Date(), 'MMM yyyy');
  const chartData = Object.values(graphDataMap)
    .sort((a, b) => a.timestamp - b.timestamp)
    .map((dataPoint) => {
      const costPerProspect =
        dataPoint.totalProspects && dataPoint.totalProspects > 0 ? dataPoint.totalSpend / dataPoint.totalProspects : 0;
      const isCurrentMonth = dataPoint.name === currentDateFormatted;

      return {
        ...dataPoint,
        costPerProspect,
        'Cost per prospect': isCurrentMonth ? 0 : costPerProspect,
        'Cost per prospect MTD': isCurrentMonth ? costPerProspect : 0,
      };
    });

  return {
    chartData,
    allPackageTypesPresent,
  };
};
